import { supabase, getActiveOrgOverride, DEFAULT_ORG_ID, applyOrgFilter } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { consentimientoValido, consentimientoVerificado, puedeUsarImagen, evaluarActivo, camposLegalesFaltantes, destinoConsentimiento, firmaCubreTorneos } from '../consent'
import { isMinor } from '../age'
import { EMBED_RESPONSABLE, conResponsable } from '../familias'
import { getLegalDocDefinition, LEGAL_DOC_KEYS } from '../../legal/documents'
import { getMisTorneosConLicencia } from './torneoInvitaciones'

export { evaluarActivo, consentimientoValido, consentimientoVerificado, puedeUsarImagen }

const cartaVersion = () => getLegalDocDefinition(LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO)?.version || 'sin_version'

// ¿El club exige consentimiento del tutor para activar jugadores?
// Default false: mientras esté apagado, el consentimiento no bloquea (transición a Fase 4).
export const orgExigeConsentimiento = async (orgId = null) => {
  try {
    const resolvedOrgId = orgId || getActiveOrgOverride()
    if (!resolvedOrgId) return { data: false, error: null }
    const { data, error } = await supabase
      .from('organizations')
      .select('exigir_consentimiento')
      .eq('id', resolvedOrgId)
      .single()
    if (error) throw error
    return { data: !!data?.exigir_consentimiento, error: null }
  } catch (error) {
    console.error('[orgExigeConsentimiento]', error)
    return { data: false, error }
  }
}

// Último consentimiento firmado (con trata_datos) del jugador.
export const getConsentimientoVigente = async (jugadorId) => {
  try {
    const { data, error } = await supabase
      .from('consentimientos')
      .select('*')
      .eq('jugador_id', jugadorId)
      .eq('estado', 'firmado')
      .eq('trata_datos', true)
      .order('fecha_hora', { ascending: false })
      .limit(1)
    if (error) throw error
    return { data: (data || [])[0] || null, error: null }
  } catch (error) {
    console.error('[getConsentimientoVigente]', error)
    return { data: null, error }
  }
}

// ¿El jugador tiene consentimiento válido para tratar sus datos?
export const jugadorTieneConsentimiento = async (player) => {
  if (!player?.id) return false
  const { data: consent } = await getConsentimientoVigente(player.id)
  return consentimientoValido(consent, {
    fechaNacimiento: player.fecha_nacimiento,
    bajoTutela: player.bajo_tutela_legal,
  })
}

// Gate de activación por consentimiento. Solo bloquea si el club lo exige.
// Devuelve { ok, error }.
export const gateConsentimientoActivo = async ({ orgId = null, jugadorId = null, player = null } = {}) => {
  let p = player
  if (!p && jugadorId) {
    const { data } = await supabase
      .from('jugadores')
      .select('id, org_id, fecha_nacimiento, bajo_tutela_legal')
      .eq('id', jugadorId)
      .single()
    p = data
  }

  const resolvedOrgId = orgId || p?.org_id
  const { data: exige } = await orgExigeConsentimiento(resolvedOrgId)
  if (!exige) return { ok: true }

  const err = (msg) => {
    const e = new Error(msg)
    e.code = 'CONSENT_REQUIRED'
    return e
  }

  if (!p) {
    return { ok: false, error: err('Este club exige el consentimiento del tutor antes de activar. El jugador quedará Pendiente hasta que se firme.') }
  }
  const tiene = await jugadorTieneConsentimiento(p)
  return tiene
    ? { ok: true }
    : { ok: false, error: err('Falta el consentimiento del tutor para activar a este jugador.') }
}

// Crea una solicitud de consentimiento con un token único (link). Agrupa por familia:
// un solo token cubre a todos los jugadores pasados (hermanos). consiente_tipo se
// deriva por edad (menor -> tutor, mayor -> jugador). Devuelve { token }.
export const crearSolicitudConsentimiento = async ({ jugadorId = null, familiaId = null, jugadorIds = null } = {}) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Una firma sobre un Aviso incompleto no prueba nada: el tutor estaría
    // aceptando un documento que no dice quién es el responsable ni a dónde
    // ejercer sus derechos ARCO. Se para aquí, en el punto único por donde pasan
    // las tres vías (link individual, envío masivo y pendientes), y ANTES de
    // generar el token -- no después de mandarle el enlace a la familia.
    const { data: org } = await supabase
      .from('organizations')
      .select('razon_social, domicilio_fiscal, correo_privacidad')
      .eq('id', orgId)
      .maybeSingle()
    const faltan = camposLegalesFaltantes(org || {})
    if (faltan.length) {
      return {
        data: null,
        error: new Error(
          `Antes de pedir firmas, completa ${faltan.join(', ')} en Configuración de la ` +
          'organización. El Aviso de Privacidad que firma el tutor se arma con esos datos.'
        ),
      }
    }

    // Filtro explícito por org (defensa en profundidad; el RLS ya lo scoping).
    let jugadores = []
    if (jugadorIds && jugadorIds.length) {
      const { data } = await supabase
        .from('jugadores')
        .select('id, fecha_nacimiento, familia_id')
        .in('id', jugadorIds)
        .eq('org_id', orgId)
        .eq('deleted', false)
      jugadores = data || []
    } else if (familiaId) {
      const { data } = await supabase
        .from('jugadores')
        .select('id, fecha_nacimiento, familia_id')
        .eq('familia_id', familiaId)
        .eq('org_id', orgId)
        .eq('deleted', false)
      jugadores = data || []
    } else if (jugadorId) {
      const { data } = await supabase
        .from('jugadores')
        .select('id, fecha_nacimiento, familia_id')
        .eq('id', jugadorId)
        .eq('org_id', orgId)
        .single()
      if (data) jugadores = [data]
    }

    if (jugadores.length === 0) {
      return { data: null, error: new Error('No hay jugadores para la solicitud.') }
    }

    // Token del link público: debe ser criptográficamente aleatorio (es la seguridad
    // del enlace). Sin fallback débil: falla cerrado si no hay CSPRNG.
    if (typeof crypto === 'undefined' || !crypto.randomUUID) {
      return { data: null, error: new Error('Tu navegador no permite generar un enlace seguro. Actualízalo e intenta de nuevo.') }
    }
    const token = crypto.randomUUID()
    const version = cartaVersion()

    const rows = jugadores.map((j) => ({
      org_id: orgId,
      jugador_id: j.id,
      familia_id: familiaId || j.familia_id || null,
      consiente_tipo: isMinor(j.fecha_nacimiento) === false ? 'jugador' : 'tutor',
      documento_version: version,
      trata_datos: false,
      usa_imagen: false,
      estado: 'enviado',
      token,
    }))

    const { error } = await supabase.from('consentimientos').insert(rows)
    if (error) throw error
    return { data: { token, jugadores: jugadores.length }, error: null }
  } catch (error) {
    console.error('[crearSolicitudConsentimiento]', error)
    return { data: null, error }
  }
}

// Datos públicos de la solicitud (para la página de firma). No requiere login.
export const getConsentimientoPublico = async (token) => {
  try {
    const { data, error } = await supabase.rpc('get_consentimiento_publico', { p_token: token })
    if (error) throw error
    return { data, error: data?.error ? new Error(data.error) : null }
  } catch (error) {
    console.error('[getConsentimientoPublico]', error)
    return { data: null, error }
  }
}

// Mensajes de la Edge Function, en lenguaje del tutor. Nunca se le muestra el
// codigo de error crudo: del otro lado hay un padre de familia, no un desarrollador.
const OTP_MENSAJES = {
  token_invalido: 'Este enlace no es válido o ya expiró.',
  ya_firmado: 'Este consentimiento ya fue firmado. No hace falta hacer nada más.',
  correo_invalido: 'Revisa el correo: no parece estar bien escrito.',
  demasiados_envios: 'Se enviaron demasiados códigos. Pide al club un enlace nuevo.',
  envio_fallido: 'No pudimos enviar el correo. Verifica la dirección e intenta de nuevo.',
  sin_codigo: 'Primero pide tu código de verificación.',
  codigo_expirado: 'El código venció. Pide uno nuevo.',
  codigo_incorrecto: 'El código no coincide. Revísalo e intenta de nuevo.',
  demasiados_intentos: 'Demasiados intentos fallidos. Pide un código nuevo.',
  otp_no_verificado: 'Falta verificar el código antes de firmar.',
  tratamiento_requerido: 'Para continuar tienes que autorizar el tratamiento de los datos.',
  nombre_requerido: 'Escribe tu nombre completo.',
  parentesco_invalido: 'Selecciona tu parentesco de la lista.',
  codigo_requerido: 'Escribe el código que te llegó por correo.',
  nada_por_firmar: 'Este consentimiento ya fue firmado o el enlace expiró.',
  // La pagina mostro un Aviso o una carta que ya no son los vigentes (se abrio antes de un
  // despliegue). La Edge no firma: la prueba tiene que corresponder al texto que se leyo.
  version_desactualizada: 'El documento se actualizó. Recarga la página para leer la versión vigente antes de firmar.',
}

const mensajeOtp = (code) => OTP_MENSAJES[code] || 'Algo salió mal. Intenta de nuevo.'

const invocarOtp = async (body) => {
  const { data, error } = await supabase.functions.invoke('consentimiento-otp', { body })
  // `invoke` marca error en cualquier status != 2xx, pero el cuerpo trae el motivo:
  // sin leerlo, "código incorrecto" y "el servidor se cayó" se verían igual.
  if (error) {
    let code = null
    try { code = (await error.context?.json())?.error } catch { /* cuerpo no legible */ }
    return { data: null, error: new Error(mensajeOtp(code)), code }
  }
  if (data?.error) return { data: null, error: new Error(mensajeOtp(data.error)), code: data.error }
  return { data, error: null, code: null }
}

// Manda el codigo de verificacion al correo que el tutor declare.
export const enviarCodigoConsentimiento = (token, correo) =>
  invocarOtp({ accion: 'enviar', token, correo })

// Verifica el codigo y firma. Registra la prueba (IP, correo verificado, datos del
// documento) y activa a los jugadores Pendientes con CURP.
//
// Ya no llama a la RPC directamente: desde 20260820 firmar exige OTP verificado y
// solo la Edge Function (service_role) puede hacerlo. Si anon pudiera invocarla, un
// curl saltaria la verificacion entera.
//
// `avisoVersion` y `cartaVersion` son las de los documentos que la pagina PINTO, no las
// vigentes de este bundle: la Edge solo firma si coinciden con las suyas (firma.ts) y si no
// responde version_desactualizada. Sin ellas viajan en null y la Edge no firma.
export const firmarConsentimientoPublico = (token, {
  codigo,
  trataDatos,
  usaImagen = false,
  autorizaMyklubb = false,
  consienteNombre,
  consienteParentesco = null,
  avisoVersion = null,
  cartaVersion = null,
} = {}) =>
  invocarOtp({
    accion: 'firmar',
    token,
    codigo,
    trata_datos: !!trataDatos,
    usa_imagen: !!usaImagen,
    autoriza_myklubb: !!autorizaMyklubb,
    consiente_nombre: consienteNombre,
    consiente_parentesco: consienteParentesco,
    aviso_version: avisoVersion,
    carta_version: cartaVersion,
  })

// Vía física: el tutor firmó en papel y el club subió el PDF.
//
// No es un caso raro ni un atajo. Quien no tiene correo ni teléfono no puede recibir
// un código, y sin esta salida el OTP no sería más estricto: sería excluyente. Hoy
// 58 de 207 jugadores de RAIDERS están así, y sin esto no se podrían activar nunca.
//
// Exige el documento subido: la firma de papel sin el papel no prueba nada.
export const registrarConsentimientoFisico = async (jugadorIds = [], {
  documentoId,
  consienteNombre,
  consienteParentesco = null,
  autorizaMyklubb = false,
  usaImagen = false,
  nota = null,
  versionCarta = null,
} = {}) => {
  try {
    if (!jugadorIds.length) return { data: null, error: new Error('Selecciona al menos un jugador.') }
    if (!documentoId) return { data: null, error: new Error('Sube primero el documento firmado.') }
    if (!String(consienteNombre || '').trim()) {
      return { data: null, error: new Error('Escribe el nombre de quien firmó.') }
    }

    const { data, error } = await supabase.rpc('registrar_consentimiento_fisico', {
      p_jugador_ids: jugadorIds,
      p_documento_id: documentoId,
      p_consiente_nombre: consienteNombre,
      p_consiente_parentesco: consienteParentesco,
      p_autoriza_myklubb: !!autorizaMyklubb,
      p_usa_imagen: !!usaImagen,
      p_nota: nota,
      // La version de la carta que se firmo (contrato C5). Con 1.1 o posterior la firma cubre
      // torneos; sin version la base la toma como 1.0.
      p_carta_version: versionCarta,
    })
    if (error) throw error
    if (data?.error) {
      const msg = {
        sin_sesion: 'Tu sesión expiró. Vuelve a entrar.',
        documento_requerido: 'Sube primero el documento firmado.',
        nombre_requerido: 'Escribe el nombre de quien firmó.',
        sin_jugadores: 'No se encontraron jugadores para registrar.',
      }[data.error] || 'No se pudo registrar el consentimiento.'
      return { data: null, error: new Error(msg) }
    }
    return { data, error: null }
  } catch (error) {
    console.error('[registrarConsentimientoFisico]', error)
    if (error?.hint === 'VERSION_INVALIDA') {
      return { data: null, error: new Error('La versión de la carta no es válida.') }
    }
    return { data: null, error }
  }
}

// Revoca el uso de imagen para difusión. Los tres documentos que firma la gente
// prometen que es revocable "en cualquier momento"; esto es lo que cumple esa
// promesa. No borra el `usa_imagen` original: anota la revocación aparte.
export const revocarUsoImagen = async (jugadorId, nota = null) => {
  try {
    const { data, error } = await supabase.rpc('revocar_uso_imagen', {
      p_jugador_id: jugadorId,
      p_nota: nota,
    })
    if (error) throw error
    if (data?.error) {
      const msg = {
        sin_sesion: 'Tu sesión expiró. Vuelve a entrar.',
        sin_acceso: 'No tienes acceso a este jugador.',
        nada_que_revocar: 'Este jugador no tiene una autorización de imagen vigente.',
      }[data.error] || 'No se pudo revocar la autorización.'
      return { data: null, error: new Error(msg) }
    }
    return { data, error: null }
  } catch (error) {
    console.error('[revocarUsoImagen]', error)
    return { data: null, error }
  }
}

// Resumen de consentimiento por jugador (para el tablero de seguimiento).
// estado: 'firmado' | 'enviado' | 'sin_solicitar'.
export const getConsentimientosResumen = async (orgId = null) => {
  try {
    let pQ = supabase
      .from('jugadores')
      // El tutor ligado (jugador_personas) y el telefono propio del jugador entran
      // para decidir A QUIEN se manda el enlace: el mayor de edad firma por si mismo,
      // el menor por su tutor. La familia es quien PAGA, y solo es el respaldo.
      .select('id, nombre_completo, familia_id, curp, estatus, fecha_nacimiento, telefono_contacto, telefono_jugador, bajo_tutela_legal, jugador_personas!left(personas!left(telefono))')
      .eq('deleted', false)
    pQ = applyOrgFilter(pQ, orgId)

    let cQ = supabase
      .from('consentimientos')
      .select('jugador_id, estado, token, fecha_hora, usa_imagen, created_at, metodo, otp_verificado_at, verificado_at, trata_datos, consiente_tipo, documento_datos')
      .order('created_at', { ascending: false })
    cQ = applyOrgFilter(cQ, orgId)

    let fQ = supabase.from('familias').select(`id, nombre, ${EMBED_RESPONSABLE}`)
    fQ = applyOrgFilter(fQ, orgId)

    // Inscripciones: dan la temporada Y el grupo de cada jugador en una sola tabla.
    // Sin esto, el tablero solo sabe filtrar por estado, y pedir firmas a los
    // inscritos de verdad obliga a identificarlos uno por uno -- en RAIDERS son 39
    // de 206, asi que el resto del padron es ruido.
    let iQ = supabase
      .from('temporada_inscripciones')
      .select('jugador_id, temporada_id, grupo_id, temporadas(id, nombre, activa), grupos(id, nombre_del_grupo)')
    iQ = applyOrgFilter(iQ, orgId)

    const [{ data: players }, { data: consents }, { data: familias }, { data: inscripciones }] =
      await Promise.all([pQ, cQ, fQ, iQ])

    const temporadasDe = {}
    const gruposDe = {}
    ;(inscripciones || []).forEach((i) => {
      if (i.temporadas) {
        (temporadasDe[i.jugador_id] = temporadasDe[i.jugador_id] || []).push(i.temporadas)
      }
      if (i.grupos) {
        (gruposDe[i.jugador_id] = gruposDe[i.jugador_id] || []).push(i.grupos)
      }
    })

    const famById = {}
    ;(familias || []).forEach((f) => { famById[f.id] = conResponsable(f) })

    // Como los consents vienen ordenados desc por created_at, el primero por jugador es el último.
    const latestByJugador = {}
    ;(consents || []).forEach((c) => { if (!latestByJugador[c.jugador_id]) latestByJugador[c.jugador_id] = c })
    // Pieza 2b: la firma que cuenta para torneos es la firmada mas reciente por fecha_hora
    // (no la ultima solicitud), como en la base.
    const firmadaByJugador = ultimaFirmadaPorJugador(consents || [])

    const rows = (players || []).map((p) => {
      const c = latestByJugador[p.id]
      const fam = p.familia_id ? famById[p.familia_id] : null
      // Una firma sin verificar ya no vale, así que el tablero no puede pintarla
      // como firmada: se vería al corriente un club al que le falta volver a
      // recabarla. Estado propio para poder filtrarlas y reenviarlas en lote.
      const estado = c?.estado === 'firmado' && !consentimientoVerificado(c)
        ? 'sin_verificar'
        : (c?.estado || 'sin_solicitar')
      return {
        jugadorId: p.id,
        nombre: p.nombre_completo,
        familiaId: p.familia_id || null,
        familiaNombre: fam?.nombre || null,
        tutorNombre: fam?.responsable?.nombre_completo || null,
        tutorTelefono: destinoConsentimiento({
          fechaNacimiento: p.fecha_nacimiento,
          bajoTutela: p.bajo_tutela_legal,
          telefonoJugador: p.telefono_jugador,
          telefonoTutorLegal: (p.jugador_personas || []).map((v) => v?.personas?.telefono).find(Boolean),
          telefonoFamilia: fam?.responsable?.telefono,
          telefonoContacto: p.telefono_contacto,
        }).telefono,
        estatus: p.estatus,
        estado,
        metodo: c?.metodo || null,
        token: c?.token || null,
        fechaFirma: c?.fecha_hora || null,
        usaImagen: c?.usa_imagen || false,
        cubreTorneos: firmaCubreTorneos(firmadaByJugador[p.id], {
          fechaNacimiento: p.fecha_nacimiento,
          bajoTutela: p.bajo_tutela_legal,
        }),
        temporadas: temporadasDe[p.id] || [],
        grupos: gruposDe[p.id] || [],
      }
    })

    return { data: rows, error: null }
  } catch (error) {
    console.error('[getConsentimientosResumen]', error)
    return { data: [], error }
  }
}

// "Solicitar a todos": crea solicitudes para los jugadores sin consentimiento aún,
// agrupando por familia (un solo token/link por familia). No re-solicita a los que
// ya tienen enviado o firmado.
export const solicitarConsentimientoPendientes = async (orgId = null) => {
  try {
    const { data: resumen } = await getConsentimientosResumen(orgId)
    const pendientes = (resumen || []).filter((r) => r.estado === 'sin_solicitar')
    if (pendientes.length === 0) return { data: { solicitudes: 0, jugadores: 0 }, error: null }

    const groups = {}
    pendientes.forEach((r) => {
      const key = r.familiaId || `solo-${r.jugadorId}`
      ;(groups[key] = groups[key] || []).push(r)
    })

    let solicitudes = 0
    let jugadores = 0
    for (const key of Object.keys(groups)) {
      const ids = groups[key].map((r) => r.jugadorId)
      const { data } = await crearSolicitudConsentimiento({ jugadorIds: ids })
      if (data?.token) {
        solicitudes += 1
        jugadores += data.jugadores || ids.length
      }
    }

    return { data: { solicitudes, jugadores }, error: null }
  } catch (error) {
    console.error('[solicitarConsentimientoPendientes]', error)
    return { data: null, error }
  }
}

// Crea solicitudes para una lista explícita de jugadores (selección del tablero),
// agrupando por familia: un solo token/link por familia (solo los seleccionados).
export const solicitarConsentimientoParaJugadores = async (jugadorIds = [], orgId = null) => {
  try {
    if (!jugadorIds.length) return { data: { solicitudes: 0, jugadores: 0 }, error: null }

    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID

    const { data: players } = await supabase
      .from('jugadores')
      .select('id, familia_id')
      .in('id', jugadorIds)
      .eq('org_id', resolvedOrgId)

    const groups = {}
    ;(players || []).forEach((p) => {
      const key = p.familia_id || `solo-${p.id}`
      ;(groups[key] = groups[key] || []).push(p.id)
    })

    let solicitudes = 0
    let jugadores = 0
    for (const key of Object.keys(groups)) {
      const ids = groups[key]
      const { data } = await crearSolicitudConsentimiento({ jugadorIds: ids })
      if (data?.token) {
        solicitudes += 1
        jugadores += data.jugadores || ids.length
      }
    }

    return { data: { solicitudes, jugadores }, error: null }
  } catch (error) {
    console.error('[solicitarConsentimientoParaJugadores]', error)
    return { data: null, error }
  }
}

// Cuales de estos jugadores NO tienen consentimiento valido. Una consulta para
// todos, no una por jugador: por aqui pasa la activacion masiva de una temporada.
export const jugadoresSinConsentimiento = async (jugadorIds = []) => {
  const ids = [...new Set(jugadorIds)].filter(Boolean)
  if (!ids.length) return { data: [], error: null }
  const [{ data: jugadores, error: jErr }, { data: consents, error: cErr }] = await Promise.all([
    supabase.from('jugadores').select('id, fecha_nacimiento, bajo_tutela_legal').in('id', ids),
    supabase.from('consentimientos').select('*')
      .in('jugador_id', ids)
      .eq('estado', 'firmado')
      .eq('trata_datos', true)
      .order('fecha_hora', { ascending: false }),
  ])
  if (jErr || cErr) return { data: null, error: jErr || cErr }

  const ultimo = {}
  ;(consents || []).forEach((c) => { if (!ultimo[c.jugador_id]) ultimo[c.jugador_id] = c })
  const sin = (jugadores || [])
    .filter((j) => !consentimientoValido(ultimo[j.id], {
      fechaNacimiento: j.fecha_nacimiento,
      bajoTutela: j.bajo_tutela_legal,
    }))
    .map((j) => j.id)
  return { data: sin, error: null }
}

// La firmada mas reciente (con trata_datos) de cada jugador: espejo de la fila que mira
// _firma_cubre_torneos en la base (ORDER BY fecha_hora DESC NULLS LAST, created_at DESC). Sin
// fecha_hora cuenta como la mas vieja; si empatan, gana la creada despues. No depende del orden
// en que lleguen las filas. OJO: no es el orden de getConsentimientoVigente ni de
// jugadoresSinConsentimiento (fecha_hora DESC a secas: en Postgres pone los NULL primero y no
// desempata); aqui tiene que coincidir con la base, o el Club diria "listo" donde la
// organizadora ve el aviso de la firma.
const momento = (v) => Date.parse(v) || 0
const esPosterior = (a, b) => (momento(a.fecha_hora) !== momento(b.fecha_hora)
  ? momento(a.fecha_hora) > momento(b.fecha_hora)
  : momento(a.created_at) > momento(b.created_at))
const ultimaFirmadaPorJugador = (consents = []) => {
  const ultima = {}
  consents
    .filter((c) => c.estado === 'firmado' && c.trata_datos)
    .forEach((c) => {
      const previa = ultima[c.jugador_id]
      if (!previa || esPosterior(c, previa)) ultima[c.jugador_id] = c
    })
  return ultima
}

const COLUMNAS_FIRMA =
  'jugador_id, estado, trata_datos, metodo, otp_verificado_at, verificado_at, consiente_tipo, fecha_hora, created_at, documento_datos'

// Cuales de estos jugadores NO tienen una firma que cubra los torneos de la red (pieza 2b; es
// un aviso, no bloquea, contrato H). Una consulta para todos. Pide documento_datos, donde la
// firma guarda cubre_torneos. Quien llama decide a quien preguntar: el roster del torneo
// (no "no convocado"), igual que la regla firma_tutor de validar_roster_inscripcion.
export const jugadoresSinFirmaParaTorneos = async (jugadorIds = []) => {
  const ids = [...new Set(jugadorIds)].filter(Boolean)
  if (!ids.length) return { data: [], error: null }
  try {
    const [{ data: jugadores, error: jErr }, { data: consents, error: cErr }] = await Promise.all([
      supabase.from('jugadores').select('id, fecha_nacimiento, bajo_tutela_legal').in('id', ids),
      supabase.from('consentimientos')
        .select(COLUMNAS_FIRMA)
        .in('jugador_id', ids)
        .eq('estado', 'firmado')
        .eq('trata_datos', true),
    ])
    if (jErr || cErr) throw jErr || cErr
    const ultima = ultimaFirmadaPorJugador(consents || [])
    const sin = (jugadores || [])
      .filter((j) => !firmaCubreTorneos(ultima[j.id], {
        fechaNacimiento: j.fecha_nacimiento,
        bajoTutela: j.bajo_tutela_legal,
      }))
      .map((j) => j.id)
    return { data: sin, error: null }
  } catch (error) {
    console.error('[jugadoresSinFirmaParaTorneos]', error)
    return { data: null, error }
  }
}

const ETAPAS_TERMINADAS = ['finalizado', 'cancelado']

// Jugadores del club que estan en el roster de un torneo de la red que sigue vivo: los que
// cuenta validar_roster_inscripcion en la regla firma_tutor (pieza 2b). Los torneos internos
// no piden firma para torneos (contrato B2). El torneo de la organizadora no se lee directo
// (su RLS no lo deja): sale de get_mis_torneos_con_licencia, que ya trae solo los de la red.
export const jugadoresEnTorneosDeRed = async (orgId = null) => {
  try {
    const resolvedOrgId = orgId || getActiveOrgOverride()
    if (!resolvedOrgId) return { data: [], error: null }

    const { data: torneos, error: tErr } = await getMisTorneosConLicencia()
    if (tErr) throw tErr
    const vivos = (torneos || [])
      .filter((t) => !ETAPAS_TERMINADAS.includes(t.etapa))
      .map((t) => t.torneo_id)
    if (!vivos.length) return { data: [], error: null }

    const { data: inscripciones, error: iErr } = await supabase
      .from('torneos_inscripciones')
      .select('id')
      .eq('club_org_id', resolvedOrgId)
      .in('torneo_id', vivos)
      .in('estatus', ['solicitada', 'aceptada'])
      .eq('activo', true)
      .is('deleted_at', null)
    if (iErr) throw iErr
    const ids = (inscripciones || []).map((i) => i.id)
    if (!ids.length) return { data: [], error: null }

    const { data: filas, error: rErr } = await supabase
      .from('torneos_inscripciones_jugadores')
      .select('jugador_id')
      .in('inscripcion_id', ids)
      .neq('status', 'no_convocado')
    if (rErr) throw rErr
    return { data: [...new Set((filas || []).map((f) => f.jugador_id))], error: null }
  } catch (error) {
    console.error('[jugadoresEnTorneosDeRed]', error)
    return { data: null, error }
  }
}
